import {
  AbstractMesh,
  IPointerEvent,
  Mesh,
  MeshBuilder,
  Observer,
  Plane,
  Quaternion,
  Ray,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { toAngle } from '@modules/standard';
import { onTouchTapObservable } from '@modules/standard/src/observable/CommonObservable';
import { zams } from '@zams/logger/zed/generated/loggerMap';
import { World } from 'miniplex';

import { TourDanjiCameraAPI } from '../../camera';
import { LayerMask } from '../../Constants';
import { MaterialManager, MaterialType } from '../../graphic/material/MaterialManager';
import { onUnitActivated } from '../../observable';
import { getTourDanjiZamsParams } from '../../TourDanjiModule';
import { UnitResponse } from '../component/response/Unit';
import { Entity } from '../Entity';

import { RaycastSystem } from './RaycastSystem';

interface DirectionObject {
  ray: Ray;
  mesh: Mesh;
  nextPosition?: Vector3 | undefined;
}

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class InteriorTourMovingSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('InteriorTourMovingSystem');
  static readonly RAY_LENGTH = 2;
  static readonly FLOOR_RAY_LENGTH = 10;
  static readonly FLOOR_CHECK_STEP = 0.3;
  static readonly MOVE_INERTIA = 0.85;
  static readonly MOVE_EPSILON = 0.001;

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------

  private readonly scene;
  private readonly materialManager;
  private readonly cameraAPI;
  private readonly parent: TransformNode;
  private compassMesh: Mesh;

  private directionObjects = new Map<number, DirectionObject>();

  private onTouchTapObserver: Observer<TouchEvent> | undefined = undefined;

  private selectedUnitResponse: UnitResponse | undefined = undefined;
  private activeUnitMeshSet = new Set<AbstractMesh>();
  private activeUnitPickables: AbstractMesh[] = [];
  private readonly floorRay: Ray;
  private moveVelocity = Vector3.Zero();
  private directionsDirty = false;
  private lastAlpha = 0;
  private lastBeta = 0;


  constructor({
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: cameraAPI,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [MaterialManager.INJECTABLE_TOKEN]: materialManager,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: TourDanjiCameraAPI;
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [RaycastSystem.INJECTABLE_TOKEN]: RaycastSystem;
    [MaterialManager.INJECTABLE_TOKEN]: MaterialManager;
  }) {
    this.scene = babylonjs.scene;
    this.cameraAPI = cameraAPI;
    this.materialManager = materialManager;
    this.parent = new TransformNode('InteriorTour', this.scene);
    this.parent.parent = this.cameraAPI.camera;
    this.compassMesh = MeshBuilder.CreatePlane("compass", {
        size: 0.4,
        sideOrientation: Mesh.FRONTSIDE
      },
      this.scene);

    this.compassMesh.parent = this.parent;
    this.compassMesh.material = this.materialManager.createMaterial(MaterialType.InteriorTourCompass);
    this.compassMesh.layerMask = LayerMask.UI;
    for (let i = 0; i < 8; i++) {
      const radian = Math.PI / 4 * i;
      const dirMesh = MeshBuilder.CreatePlane(`dir:${radian * toAngle}`, { width: 0.15, height: 0.15 }, this.scene);
      dirMesh.isPickable = false;
      dirMesh.material = this.materialManager.createMaterial(MaterialType.InteriorTourArrow);
      dirMesh.parent = this.parent;
      dirMesh.rotationQuaternion = Quaternion.RotationAxis(Vector3.LeftHandedBackwardReadOnly, radian);
      dirMesh.position = Vector3.UpReadOnly.applyRotationQuaternion(Quaternion.RotationAxis(Vector3.LeftHandedBackwardReadOnly, radian)).scale(0.3);
      dirMesh.layerMask = LayerMask.UI;
      this.directionObjects.set(radian, { ray: new Ray(Vector3.ZeroReadOnly, Vector3.ZeroReadOnly, InteriorTourMovingSystem.RAY_LENGTH), mesh: dirMesh });
    }

    this.floorRay = new Ray(Vector3.ZeroReadOnly, Vector3.DownReadOnly, InteriorTourMovingSystem.FLOOR_RAY_LENGTH);

    this.onTouchTapObserver = onTouchTapObservable.add((event) => {
      this.whenTouchTap(event);
    });

    onUnitActivated.add((unit) => {
      this.whenUnitActivated(unit);
    });
  }

  private checkAvailableViewType = () => {
    const cameraType = this.cameraAPI.getCurrentCameraViewType();
    if (cameraType === 'UnitWindowView' || cameraType === 'FakeUnitWindowView') {
      return true;
    }
    return false;
  }

  private whenBeforeRender = () => {
    this.parent.setEnabled(this.checkAvailableViewType());
    if (!this.parent.isEnabled()) {
      this.moveVelocity.setAll(0);
      return;
    }

    const engine = this.scene.getEngine();
    const canvas = engine.getRenderingCanvas()!;
    const compassWidthRate = 0.8 + Math.min(canvas.width / 1920, 1.0);
    const compassHeightRate = 2.5 + Math.min(canvas.height / 1080, 1.0);

    this.parent.rotation = new Vector3(Math.PI / compassHeightRate, 0, 0);
    this.parent.position = new Vector3(0, -0.5, 1.5);
    this.parent.scaling = Vector3.One().scale(compassWidthRate);

    this.applyMovement();
  }

  private readonly wallRay = new Ray(Vector3.ZeroReadOnly, Vector3.ZeroReadOnly, 0);

  private isMoving = false;

  private applyMovement = () => {
    if (this.moveVelocity.lengthSquared() < InteriorTourMovingSystem.MOVE_EPSILON) {
      if (this.isMoving) {
        this.isMoving = false;
        this.directionsDirty = true; // 이동 완료 후 새 위치에서 방향 재계산
      }
      this.moveVelocity.setAll(0);
      return;
    }

    this.isMoving = true;
    const camera = this.cameraAPI?.camera;
    if (!camera) {
      this.stopMovement();
      return;
    }

    // 벽 충돌 검사: 이동 방향으로 벽이 있으면 정지
    const moveLen = this.moveVelocity.length();
    const moveDir = this.moveVelocity.normalizeToNew();
    this.wallRay.origin = camera.target;
    this.wallRay.direction = moveDir;
    this.wallRay.length = moveLen + 0.1;
    const wallHit = this.pickFromActiveMeshes(this.wallRay, true);

    // DEBUG: 벽 충돌 로그
    // const wallCandidates = this.activeUnitPickables.filter(m => (m.layerMask & LayerMask.UnitCollider) === LayerMask.UnitCollider);
    // console.log(`[WallCheck] origin=(${camera.target.x.toFixed(3)},${camera.target.y.toFixed(3)},${camera.target.z.toFixed(3)}) dir=(${moveDir.x.toFixed(3)},${moveDir.y.toFixed(3)},${moveDir.z.toFixed(3)}) rayLen=${(moveLen + 0.1).toFixed(3)} candidates=${wallCandidates.length} hit=${!!wallHit}${wallHit ? ` dist=${wallHit.distance.toFixed(3)} mesh=${wallHit.pickedMesh.name}` : ''}`);

    if (wallHit) {
      this.stopMovement();
      return;
    }

    // 바닥 검사: 바닥 밖이면 정지
    const nextTarget = camera.target.add(this.moveVelocity);
    this.floorRay.origin = nextTarget;
    this.floorRay.direction = Vector3.DownReadOnly;
    const floorHit = this.pickFromActiveMeshes(this.floorRay, false);
    if (!floorHit?.pickedPoint) {
      this.stopMovement();
      return;
    }

    camera.target.addInPlace(this.moveVelocity);
    this.moveVelocity.scaleInPlace(InteriorTourMovingSystem.MOVE_INERTIA);
  }

  private stopMovement = () => {
    this.moveVelocity.setAll(0);
    this.isMoving = false;
    this.directionsDirty = true;
  }

  private whenViewMatrixChanged = () => {
    if (!this.parent.isEnabled()) return;

    const camera = this.cameraAPI?.camera;
    if (!camera) return;
    this.compassMesh.rotationQuaternion = Quaternion.RotationAxis(Vector3.LeftHandedBackwardReadOnly, (camera.alpha + Math.PI / 2));

    if (this.cameraAPI.getCurrentCameraViewType() !== 'UnitWindowView') {
      this.directionObjects.forEach((obj) => {
        obj.mesh.setEnabled(false);
      });
    }

    // 회전 변화 감지 (이동에 의한 viewMatrix 변경과 구분)
    if (camera.alpha !== this.lastAlpha || camera.beta !== this.lastBeta) {
      this.directionsDirty = true;
      this.lastAlpha = camera.alpha;
      this.lastBeta = camera.beta;
    }
  }

  /** viewMatrixChangedEnd: 카메라 조작 종료 후 150ms debounce로 1회 실행 */
  private whenViewMatrixChangedEnd = () => {
    if (!this.parent.isEnabled()) return;
    if (this.cameraAPI.getCurrentCameraViewType() !== 'UnitWindowView') return;
    if (!this.directionsDirty) return;
    this.directionsDirty = false;

    this.directionObjects.forEach((obj, radian) => {
      const nextPosition = this.computeNextMovingPosition(obj, radian);
      obj.mesh.setEnabled(!!nextPosition);
    });
  }

  private computeNextMovingPosition = (obj: DirectionObject, radian: number) => {
    const camera = this.cameraAPI?.camera;
    if (!camera) return undefined;

    // 카메라 전방을 기준으로 ray를 쏠 방향을 조정
    const forward = Vector3.TransformNormal(Vector3.LeftHandedForwardReadOnly, camera._worldMatrix);
    const forwardXZ = new Vector3(forward.x, 0, forward.z).normalize();
    const dir = forwardXZ.applyRotationQuaternion(Quaternion.RotationAxis(Vector3.UpReadOnly, radian));
    obj.ray.origin = camera.position;
    obj.ray.direction = dir;

    // 벽 감지: 활성 유닛 메시만 직접 순회
    const wallHit = this.pickFromActiveMeshes(obj.ray, true);
    const length = wallHit?.pickedPoint ? Vector3.Distance(camera.position, wallHit.pickedPoint) - 0.1 : InteriorTourMovingSystem.RAY_LENGTH;

    return this.checkFloorHitPosition(camera.position, dir, length, 1.2);
  }

  private checkFloorHitPosition = (origin: Vector3, direction: Vector3, length: number, lengthMin: number): Vector3 | undefined => {
    if (!direction || length < lengthMin) {
      return undefined;
    }

    const checkPoint = origin.add(direction.scale(length));
    this.floorRay.origin = checkPoint;
    this.floorRay.direction = Vector3.DownReadOnly;

    // 바닥 감지: 활성 유닛 메시만 직접 순회 (scene 전체 순회 방지)
    const floorHit = this.pickFromActiveMeshes(this.floorRay, false);

    if (floorHit?.pickedPoint) {
      if (Vector3.Distance(origin, floorHit.pickedPoint) < lengthMin) return undefined;
      return floorHit.pickedPoint;
    }
    return this.checkFloorHitPosition(origin, direction, length - InteriorTourMovingSystem.FLOOR_CHECK_STEP, lengthMin);
  }

  /**
   * scene.pickWithRay 대신 활성 유닛 메시만 직접 순회하여 Ray 교차 판정.
   * scene 전체 메시(250+)를 순회하지 않고 활성 유닛 메시(~24)만 체크.
   *
   * @param wallOnly true면 UnitCollider 레이어 메시만 체크 (벽 감지용)
   */
  private pickFromActiveMeshes = (ray: Ray, wallOnly: boolean) => {
    let closest: { pickedPoint: Vector3; pickedMesh: AbstractMesh; distance: number } | undefined;

    for (const mesh of this.activeUnitPickables) {
      if (wallOnly && (mesh.layerMask & LayerMask.UnitCollider) !== LayerMask.UnitCollider) continue;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const hit = ray.intersectsMesh(mesh as any, false);
      if (hit.hit && hit.pickedPoint) {
        if (!closest || hit.distance < closest.distance) {
          closest = { pickedPoint: hit.pickedPoint, pickedMesh: mesh, distance: hit.distance };
        }
      }
    }

    return closest;
  }

  private whenTouchTap = (event: TouchEvent | IPointerEvent) => {
    const isTouchEvent = 'TouchEvent' in window && event instanceof TouchEvent;
    const x = isTouchEvent ? event.changedTouches[0]?.clientX : (event as PointerEvent).clientX;
    const y = isTouchEvent ? event.changedTouches[0]?.clientY : (event as PointerEvent).clientY;
    if (x === undefined || y === undefined) return;
    const camera = this.cameraAPI?.camera;
    if (!camera) return;
    const pivot = this.parent.getAbsolutePosition();
    const pos = this.projectClickToPlane(x, y, Vector3.Up(), pivot.y);
    if (!pos) return;
    const dir0 = pos.subtract(pivot);
    const proj = new Vector3(dir0.x, 0, dir0.z).normalize();
    let csMax = -1;
    let dir = Vector3.Zero();
    const forward = camera.getDirection(Vector3.LeftHandedForwardReadOnly);
    const forwardXZ = new Vector3(forward.x, 0, forward.z).normalize();
    let validDir = false;
    this.directionObjects.forEach((obj, radian) => {
      const direction = forwardXZ.applyRotationQuaternion(Quaternion.RotationAxis(Vector3.UpReadOnly, radian));
      const cs = proj.cosSimilarity(direction);
      if (csMax < cs) {
        csMax = cs;
        dir = direction;
        validDir = obj.mesh.isEnabled();
      }
    });
    if (!validDir) return;

    // 이동 시작: moveVelocity 설정 → beforeRender에서 매 프레임 적용 + floor validation
    this.moveVelocity = dir.normalize().scale(0.3);
    zams.z142({
      ...getTourDanjiZamsParams(),
      zed_dong: this.selectedUnitResponse?.unitMetadata.동 ?? '',
      zed_ho: this.selectedUnitResponse?.unitMetadata.호 ?? '',
    }).send();
  }

  private whenUnitActivated = (unit: UnitResponse | undefined) => {
    this.selectedUnitResponse = unit;
    this.activeUnitMeshSet.clear();
    this.activeUnitPickables = [];
    this.moveVelocity.setAll(0);
    this.directionsDirty = true;

    if (unit?.unitMeshes) {
      for (const mesh of unit.unitMeshes) {
        if (mesh) {
          this.activeUnitMeshSet.add(mesh);
          for (const child of mesh.getChildMeshes(false)) {
            this.activeUnitMeshSet.add(child);
          }
        }
      }
    }

    // pickable 메시만 필터링하여 캐싱 (vertices 0인 루트 메시 제외)
    for (const mesh of this.activeUnitMeshSet) {
      if (mesh.isPickable && mesh.getTotalVertices() > 0) {
        this.activeUnitPickables.push(mesh);
      }
    }
  }

  private projectClickToPlane(clickX: number, clickY: number, planeNormal: Vector3, planeHeight: number): Vector3 | undefined {
    const ray = this.scene.createPickingRay(clickX, clickY, null, null);
    const plane = Plane.FromPositionAndNormal(new Vector3(0, planeHeight, 0), planeNormal);
    const distance = ray.intersectsPlane(plane);
    if (!distance) return undefined;

    return ray.origin.add(ray.direction.scale(distance));
  }

  dispose() {
    this.directionObjects.forEach((obj) => {
      obj.mesh.dispose();
    });
    this.compassMesh.dispose();
    this.parent.dispose();
    this.onTouchTapObserver?.remove();
  }

  readonly operations: SystemOperationDefinition[] = [
    {
      group: 'viewMatrixChanged',
      run: this.whenViewMatrixChanged,
    },
    {
      group: 'viewMatrixChangedEnd',
      run: this.whenViewMatrixChangedEnd,
    },
    {
      group: 'beforeRender',
      run: this.whenBeforeRender,
    }
  ];
}
